// ══════════════════════════════════════════════════════════════
// COMPASS – Authentication Service (Keycloak Wrapper)
// ══════════════════════════════════════════════════════════════
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, EMPTY, defer, from, of, throwError } from 'rxjs';
import { catchError, map, retry, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import Keycloak from 'keycloak-js';
import { environment } from '../../../environments/environment';
import { AuthUser } from '../models/auth-user.model';
import type { AppState } from '../store/app.state';
import { UserActions } from '../store/user/user.actions';
import {
  extractUserFromToken,
  isTokenExpired,
} from '../utils/jwt-decoder';
import { Logger } from './logger.service';
import { activeLocale } from '../i18n/active-locale';
import { DEFAULT_LANGUAGE_CONFIG, getLanguageConfig } from '../i18n/languages.config';

export interface LoginResponse {
  user: AuthUser;
  tokenExpiresAt: number;
  refreshTokenExpiresAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(Logger);
  private readonly store = inject(Store<AppState>);
  private keycloak = new Keycloak({
    url: environment.keycloak.url,
    realm: environment.keycloak.realm,
    clientId: environment.keycloak.clientId,
  });

  constructor() {
    this.keycloak.onTokenExpired = () => {
      this.handleTokenExpired();
    };
  }

  /**
   * Initialize Keycloak instance with Authorization Code Flow + PKCE
   * @returns Promise that resolves to true if user is authenticated
   */
  async init(): Promise<boolean> {
    try {
      // Initialize Keycloak with check-sso
      // Uses Authorization Code Flow with PKCE (most secure)
      const authenticated = await this.keycloak.init({
        onLoad: 'check-sso',
        checkLoginIframe: false,
        pkceMethod: 'S256', // Enable PKCE with SHA-256
        flow: 'standard', // Authorization Code Flow
      });

      return authenticated;
    } catch (error) {
      this.logger.error('Keycloak initialization failed:', error);
      return false;
    }
  }

  /**
   * Login using Authorization Code Flow with PKCE
   * Redirects to Keycloak's hosted login page
   */
  login(): void {
    const returnUrl = this.consumeReturnUrl();
    this.keycloak.login({
      redirectUri: window.location.origin + returnUrl,
      // Keycloak login page language (ui_locales); ignored if the realm doesn't support it.
      // Reads activeLocale instead of injecting LanguageService: AuthService is injected by the
      // auth interceptor, which runs for the translation file request itself, so depending on
      // TranslateService here would create a DI cycle and break translation loading.
      locale: (getLanguageConfig(activeLocale()) ?? DEFAULT_LANGUAGE_CONFIG).keycloakLocale,
    });
  }

  /**
   * Logout the current user
   */
  logout(): Observable<void> {
    return from(
      this.keycloak.logout({
        redirectUri: window.location.origin + '/auth/login',
      })
    ).pipe(
      tap(() => {
        this.clearSession();
      }),
      map(() => void 0),
      catchError((error) => {
        this.logger.error('Logout failed:', error);
        // Even if server logout fails, clear local state.
        this.clearSession();
        return of(void 0);
      })
    );
  }

  /**
   * Refresh the access token using the refresh token
   */
  refreshToken(minValiditySeconds = 60): Observable<boolean> {
    if (!this.keycloak.refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    return defer(() => from(this.keycloak.updateToken(minValiditySeconds))).pipe(
      map(() => !!this.keycloak.token),
      catchError((error) => {
        this.logger.error('Token refresh failed:', error);
        return throwError(() => new Error('Token refresh failed'));
      })
    );
  }

  /**
   * Ensures a non-expired token is available before an authenticated API call.
   */
  ensureValidToken(minValiditySeconds = 30): Observable<string | undefined> {
    const token = this.keycloak.token;
    if (!token) {
      return of(undefined);
    }

    return this.refreshToken(minValiditySeconds).pipe(
      map(() => this.keycloak.token ?? token),
      catchError((error) => {
        this.logger.error('Ensuring valid token failed:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Clear local authentication state without forcing a full Keycloak round-trip.
   */
  clearSession(): void {
    if (this.keycloak.token || this.keycloak.refreshToken) {
      this.keycloak.clearToken();
    }
  }

  /**
   * Get current user from token
   */
  getCurrentUser(): AuthUser | null {
    if (!this.keycloak.token) {
      return null;
    }

    if (!this.isAuthenticated()) {
      return null;
    }

    return extractUserFromToken(this.keycloak.token);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    if (!this.keycloak.token || !this.keycloak.authenticated) {
      return false;
    }

    try {
      return !this.keycloak.isTokenExpired(0);
    } catch {
      return !isTokenExpired(this.keycloak.token);
    }
  }

  /**
   * Get current access token
   */
  getToken(): string | undefined {
    return this.keycloak.token;
  }

  /**
   * Send forgot password email
   * Note: Keycloak doesn't have a direct API for this.
   * This would typically redirect to Keycloak's forgot password page
   * or use a custom backend endpoint.
   */
  forgotPassword(email: string): Observable<void> {
    // This is a placeholder - implement based on your backend API
    // Option 1: Redirect to Keycloak's forgot password page
    // const url = `${environment.keycloak.url}realms/${environment.keycloak.realm}/login-actions/reset-credentials`;
    // window.location.href = url;

    // Option 2: Use custom backend endpoint
    return this.http
      .post<void>(`${environment.api.baseUrl}/auth/forgot-password`, { email })
      .pipe(
        catchError((error) => {
          this.logger.error('Forgot password failed:', error);
          return throwError(
            () => new Error('AUTH.FORGOT_PASSWORD.FAILED')
          );
        })
      );
  }

  /**
   * Reset password with token
   * Note: This typically requires a custom backend endpoint
   */
  resetPassword(token: string, newPassword: string): Observable<void> {
    // This is a placeholder - implement based on your backend API
    return this.http
      .post<void>(`${environment.api.baseUrl}/auth/reset-password`, {
        token,
        newPassword,
      })
      .pipe(
        catchError((error) => {
          this.logger.error('Reset password failed:', error);
          return throwError(() => new Error('AUTH.RESET_PASSWORD.FAILED'));
        })
      );
  }

  private consumeReturnUrl(): string {
    const storedReturnUrl = sessionStorage.getItem('compass_return_url') ?? '';
    sessionStorage.removeItem('compass_return_url');

    if (!storedReturnUrl) {
      return '';
    }

    try {
      const parsed = new URL(storedReturnUrl, window.location.origin);
      if (parsed.origin !== window.location.origin) {
        return '';
      }
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return storedReturnUrl.startsWith('/') ? storedReturnUrl : '';
    }
  }

  private handleTokenExpired(): void {
    this.refreshToken(0)
      .pipe(
        retry({ count: 2, delay: 30_000 }),
        catchError((error) => {
          this.logger.error('Automatic token refresh failed after retries:', error);
          this.store.dispatch(UserActions.sessionExpired());
          return EMPTY;
        })
      )
      .subscribe();
  }
}
